"use client";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { SignupStep } from "@/hooks/useSignupFunnel";
import { cn } from "@/lib/utils";
import type { EventSource } from "@/types/discover";
import { attendanceEnterHref } from "@/utils/attendanceLink";
import type { EventHost } from "@/utils/eventHost";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import Cta from "./Cta";
import SignupFunnel from "./SignupFunnel";

type CtaVariant = NonNullable<React.ComponentProps<typeof Cta>["variant"]>;

interface PublicEventSignupCtaProps {
  /** CTA label, e.g. "Join event" or "Fully booked". */
  label: string;
  /** Bottom-sheet accessible title (visually hidden; steps show their own). */
  sheetTitle: string;
  /** Bottom-sheet accessible description (visually hidden). */
  sheetDescription: string;
  /** Accessible label for the close control. */
  closeLabel: string;
  /** Active locale, forwarded to credential proxies in the funnel. */
  locale: string;
  /** When set, the sheet opens at this step instead of waiting for a click. */
  initialStep?: SignupStep;
  event: EventSource;
  host: EventHost;
  variant?: CtaVariant;
  /** Renders the CTA disabled so it cannot open the sheet. */
  disabled?: boolean;
}

/**
 * CTA for public (group-backed) events: opens the signup funnel in a bottom
 * sheet instead of navigating to an external signup page. What the button says
 * and whether it is enabled is decided by the caller (`EventCta`).
 */
export default function PublicEventSignupCta({
  label,
  sheetTitle,
  sheetDescription,
  closeLabel,
  locale,
  initialStep,
  event,
  host,
  variant = "brand",
  disabled = false,
}: PublicEventSignupCtaProps) {
  const [open, setOpen] = useState(!!initialStep);

  useEffect(() => {
    if (initialStep) setOpen(true);
  }, [initialStep]);

  // Set once a signup is confirmed: the attendee's own attendance ("custom
  // link") page. Closing the sheet then sends them there instead of leaving
  // them on the unchanged "Join us" view.
  const [attendanceHref, setAttendanceHref] = useState<string | null>(null);

  // Any close after a confirmed signup navigates to the attendance page (a full
  // navigation so the `/attendance/enter` handler can set the token cookie and
  // redirect to the clean view). Before signup, close just dismisses the sheet.
  function handleClose() {
    if (attendanceHref) {
      window.location.assign(attendanceHref);
      return;
    }
    setOpen(false);
  }

  return (
    <>
      <Cta
        label={label}
        variant={variant}
        disabled={disabled}
        onClick={() => setOpen(true)}
        ctaKind="public_event"
        clubId={host.clubId}
        eventId={event.id}
        groupId={host.groupId}
        sourceType={host.sourceType}
      />
      <Dialog
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : handleClose())}
      >
        <DialogContent
          showCloseButton={false}
          // Only Escape dismisses the funnel — a stray click outside must not
          // discard everything the user has entered.
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            "inset-x-0 bottom-0 left-auto top-auto flex h-[92dvh] max-h-none w-full max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-t-2xl rounded-b-none p-4",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-125 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:overflow-y-auto sm:rounded-lg sm:p-6",
          )}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{sheetTitle}</DialogTitle>
            <DialogDescription>{sheetDescription}</DialogDescription>
          </DialogHeader>
          <DialogClose
            aria-label={closeLabel}
            className="absolute right-4 top-4 rounded-full p-1 hover:bg-background-secondary"
          >
            <X className="h-5 w-5" aria-hidden />
          </DialogClose>
          <SignupFunnel
            event={event}
            locale={locale}
            initialStep={initialStep}
            onClose={handleClose}
            onSignupComplete={(attendanceUrl) =>
              setAttendanceHref(
                attendanceUrl
                  ? attendanceEnterHref({
                      locale,
                      eventId: event.id,
                      attendanceUrl,
                      basePath: process.env.NEXT_PUBLIC_BASE_PATH ?? "",
                    })
                  : null,
              )
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
